import {
  Alert,
  Badge,
  Button,
  DetailSurface,
  StatusDot
} from "@vjal/ui";
import { DataTableCollection } from "@vjal/ui";
import type { DataTableColumn } from "@vjal/ui";
import { Download } from "lucide-react";
import { useState } from "react";
import { CapabilityButton } from "../components/AccessState";
import { evaluateActionCapability, type ActionCapabilityDecision } from "../domain/capabilities";
import type { CoachingRole } from "../domain/roles";
import { canRoleDownloadReport, filterReadableReports, isStaffRole } from "../domain/sensitive-surfaces";
import { reportTypeLabels } from "../domain/report-taxonomy";
import type { CoachingArtifactDto, CoachingReportType, CoachingSessionDto } from "../services/coaching-dtos";
import { filterReadableReportDtos } from "./reports/reports-helpers";
import { downloadReportFile } from "./reports/report-detail/report-download-section";
import { useReportMarkReviewedMutation } from "../hooks/useCoachingOrgAnalytics";
import { isLiveCoachingDataMode, useLiveCoachingList } from "../hooks/useLiveProductData";
import { liveReportTableRows } from "./product-live-rows";
import type { ProductScreenContract, ProductScreenContext, TableRow } from "./product-route-contracts";

const visibilityLabels: Record<string, string> = {
  company_readable: "Company readable",
  coach_only: "Coach only",
  staff_only: "Staff only",
  staff_and_owner: "Staff + owner",
  participant_readable: "Participant readable"
};

export function orgReportsScreen(): ProductScreenContract {
  return {
    title: () => "Org Reports",
    primaryJob:
      "Org-scoped report library with readable report types only and role-scoped row actions.",
    secondaryJob: "Cohort leads get the same list scoped to assigned cohorts, download-only.",
    workTitle: "Org report library",
    rows: orgReportFixtureRows(),
    columns: orgReportColumns("company_admin"),
    states: ["loading", "empty", "denied", "read-only", "download-authorized", "review-enabled"],
    apiFamilies: ["artifact-service"],
    roleDeltas:
      "Company admin: team_admin/company_readable, Mark reviewed + Download. Cohort lead: assigned cohorts, Download only (Mark reviewed denied). Confidential/coach/internal types never appear.",
    extensionSlots: ["per-row signed download", "pagination", "audience filter", "review audit"],
    mobilePosture: "Report rows, type badges, and row actions stack; the type filter precedes the table.",
    evidenceRequirement: "Playwright must prove company_admin sees team_admin rows AND never coach_feedback/vjal_internal/ceo_confidential (present-AND-absent).",
    vjalComponents: ["Alert","Badge","Button","DataTableCollection","DetailSurface","StatusDot"],
    localCompositions: ["org-reports focused product surface", "CapabilityButton"],
    packageGapRefs: [],
    decisionHierarchy: {
      firstViewport: "The D-3-scoped report library for this org/company.",
      primaryAction: "Open a report, mark it reviewed, or download it within role scope.",
      secondaryPanel: "Audience/type filter restricted to readable types.",
      deferredSurface: "Bulk export bundle and pagination land with the live export route.",
      gatedControls: "Mark reviewed enabled for company_admin only; cohort_lead is download-only."
    },
    requiredViewports: ["desktop", "mobile"],
    closureState: "focused",
    closureOwner: "VIL-20260616-0041",
    surfaceMode: "focused",
    customBody: OrgReportsSurface
  };
}

function OrgReportsSurface({ role }: ProductScreenContext) {
  const live = isLiveCoachingDataMode();
  const [typeFilter, setTypeFilter] = useState<"all" | CoachingReportType>("all");
  const [downloadPendingId, setDownloadPendingId] = useState<string | null>(null);
  const [downloadError, setDownloadError] = useState<string | null>(null);
  const liveReports = useLiveCoachingList("reports.list");
  const liveSessions = useLiveCoachingList("sessions.list");
  const markReviewed = useReportMarkReviewedMutation();

  const visibleRows = buildOrgReportVisibleRows({
    role,
    live,
    reports: liveReports.items,
    sessions: liveSessions.items,
    typeFilter
  });
  const reviewDecision = evaluateActionCapability(role, "reports:mark-reviewed");
  const availableTypes = uniqueTypes(visibleRows);
  const download = {
    isPending: downloadPendingId !== null,
    mutate: async (row: TableRow) => {
      if (downloadPendingId !== null) return;
      const sessionId = typeof row.sessionId === "string" ? row.sessionId : null;
      if (!sessionId) {
        setDownloadError("This report is not linked to a session and cannot be downloaded.");
        return;
      }
      setDownloadPendingId(row.id);
      setDownloadError(null);
      try {
        await downloadReportFile(sessionId, `coaching-report-${row.id}.html`, row.id);
      } catch (cause) {
        setDownloadError(cause instanceof Error && cause.message.trim() ? cause.message : "The report could not be downloaded. Try again.");
      } finally {
        setDownloadPendingId(null);
      }
    }
  };
  return (
    <section aria-label="Org reports" className="col-span-12 flex flex-col gap-4" data-testid="org-reports-page">
      {/* F3: real @vjal/ui DetailSurface for Detail-1 org-reports. */}
      <DetailSurface className="col-span-12">
        <div className="space-y-4">
          <Alert variant="info">
            {role === "cohort_lead"
              ? "Cohort lead sees assigned-cohort team reports, download only."
              : "Only team reports in your assigned scope are listed here."}
          </Alert>
          <div className="flex flex-wrap gap-2" role="group" aria-label="Report type filter">
            <Button type="button" variant={typeFilter === "all" ? "primary" : "ghost"} onClick={() => setTypeFilter("all")}>
              All
            </Button>
            {availableTypes.map((type) => (
              <Button key={type} type="button" variant={typeFilter === type ? "primary" : "ghost"} onClick={() => setTypeFilter(type)}>
                {reportTypeLabels[type]}
              </Button>
            ))}
          </div>
          <div className="max-w-full overflow-x-auto"><div className="min-w-[1040px]"><DataTableCollection
            caption="Org report library"
            columns={orgReportColumns(role, { reviewDecision, markReviewed, download })}
            data={visibleRows}
            loading={live && liveReports.isLoading}
            emptyTitle={typeFilter === "all" ? "No reports yet" : "No matching reports"}
            emptyMessage={typeFilter === "all" ? "No team reports in your scope yet — they will appear here once generated." : "No rows match your filter."}
            stableChipNames /></div></div>
          {downloadError ? <Alert variant="error" data-testid="org-report-download-error">{downloadError}</Alert> : null}
          {markReviewed.isSuccess ? <StatusDot tone="ok" label="Report marked reviewed" /> : null}
        </div>
      </DetailSurface>
    </section>
  );
}

/** Live/mock org-report pipeline: DTO D-3 filter → table projection → type chip filter. */
export function buildOrgReportVisibleRows({
  role,
  live,
  reports,
  sessions,
  typeFilter = "all"
}: {
  role: CoachingRole;
  live: boolean;
  reports: CoachingArtifactDto[];
  sessions: CoachingSessionDto[];
  typeFilter?: "all" | CoachingReportType;
}): Array<TableRow & { reportType?: CoachingReportType }> {
  const scopedReports = live ? filterReadableReportDtos(role, reports) : reports;
  const sourceRows = live ? liveReportTableRows(scopedReports, sessions) : orgReportFixtureRows();
  // D-3 belt-and-suspenders on projected rows (mock fixtures + any mapper regression).
  const readableRows = filterReadableReports(role, sourceRows as Array<TableRow & { reportType?: CoachingReportType }>);
  return typeFilter === "all" ? readableRows : readableRows.filter((row) => row.reportType === typeFilter);
}

function orgReportFixtureRows(): Array<TableRow & { reportType?: CoachingReportType; visibility?: string; deliveryMode?: string; downloadPolicy?: string; reviewedAt?: string | null; sessionId?: string }> {
  return [
    { id: "report-1", report: "Session 1 Team Report", session: "Session 1", sessionId: "session-1", cohort: "Spring Leaders", status: "ready", reportType: "team_admin", visibility: "company_readable", deliveryMode: "in_dashboard", downloadPolicy: "company_readable", reviewedAt: null },
    { id: "report-coach-1", report: "Session 1 Coach Feedback", session: "Session 1", sessionId: "session-1", cohort: "Spring Leaders", status: "ready", reportType: "coach_feedback", visibility: "coach_only", deliveryMode: "in_dashboard", downloadPolicy: "coach_only", reviewedAt: null },
    { id: "report-internal-1", report: "Session 1 VJAL Internal Review", session: "Session 1", sessionId: "session-1", cohort: "Spring Leaders", status: "ready", reportType: "vjal_internal", visibility: "staff_only", deliveryMode: "in_dashboard", downloadPolicy: "staff_only", reviewedAt: null },
    { id: "report-ceo-1", report: "Session 1 CEO Confidential", session: "Session 1", sessionId: "session-1", cohort: "Spring Leaders", status: "ready", reportType: "ceo_confidential", visibility: "staff_only", deliveryMode: "ceo_out_of_band", downloadPolicy: "staff_only", reviewedAt: null }
  ];
}

function uniqueTypes(rows: Array<{ reportType?: CoachingReportType }>): CoachingReportType[] {
  const seen: Partial<Record<CoachingReportType, true>> = {};
  const types: CoachingReportType[] = [];
  for (const row of rows) {
    if (!row.reportType || seen[row.reportType]) continue;
    seen[row.reportType] = true;
    types.push(row.reportType);
  }
  return types;
}

function audienceTone(type: CoachingReportType | undefined) {
  if (type === "team_admin") return "success" as const;
  if (type === "coach_feedback") return "info" as const;
  return "warning" as const;
}

interface OrgReportMutationHandle {
  isPending: boolean;
  mutate: (variables: { reportId: string; decision?: "reviewed" | "approved" }) => unknown;
}

interface OrgReportDownloadHandle {
  isPending: boolean;
  mutate: (row: TableRow) => unknown;
}

interface OrgReportRowActions {
  reviewDecision: ActionCapabilityDecision;
  markReviewed: OrgReportMutationHandle;
  download: OrgReportDownloadHandle;
}

function orgReportColumns(role: CoachingRole, actions?: OrgReportRowActions): DataTableColumn<TableRow>[] {
  const columns: DataTableColumn<TableRow>[] = [
    { key: "report", header: "Report" },
    { key: "cohort", header: "Cohort" },
    {
      key: "reportType",
      header: "Audience",
      render: (row) => {
        const type = row.reportType as CoachingReportType | undefined;
        return (
          <span data-report-type={type ?? "team_admin"}>
            <Badge variant={audienceTone(type)}>{type ? reportTypeLabels[type] : "Team / company"}</Badge>
          </span>
        );
      }
    },
    {
      key: "visibility",
      header: "Visibility",
      render: (row) => <span className="text-sm text-ink-muted">{visibilityLabels[String(row.visibility ?? "company_readable")] ?? String(row.visibility)}</span>
    },
    { key: "status", header: "Status", render: (row) => <StatusDot tone={row.reviewedAt ? "ok" : "info"} label={row.reviewedAt ? "reviewed" : String(row.status)} /> }
  ];

  if (actions) {
    columns.push({
      key: "actions",
      header: "Actions",
      render: (row) => {
        const downloadAllowed = canRoleDownloadReport(role, {
          reportType: row.reportType as CoachingReportType | undefined,
          visibility: row.visibility as string | undefined,
          deliveryMode: row.deliveryMode as string | undefined,
          downloadPolicy: row.downloadPolicy as string | undefined
        });
        return (
          <div className="flex flex-wrap items-center gap-2">
            {isStaffRole(role) || role === "company_admin" || role === "coach" ? (
              <CapabilityButton
                decision={actions.reviewDecision}
                loading={actions.markReviewed.isPending}
                onClick={() => actions.markReviewed.mutate({ reportId: row.id })}
                data-testid="org-report-mark-reviewed"
              >
                Mark reviewed
              </CapabilityButton>
            ) : null}
            <Button
              type="button"
              variant="secondary"
              leftIcon={<Download size={16} />}
              disabled={!downloadAllowed}
              loading={actions.download.isPending}
              onClick={downloadAllowed ? () => actions.download.mutate(row) : undefined}
              data-testid="org-report-download"
            >
              Download
            </Button>
          </div>
        );
      }
    });
  }

  return columns;
}
